
import { useState } from 'react';

export default function ProcessToggle() {
  const [activeTab, setActiveTab] = useState<'online' | 'broker'>('online');

  const onlineSteps = [
    {
      number: '01',
      title: 'Find Your Vehicle',
      feeling: 'No one will follow you around the lot asking if you have any questions every 90 seconds.',
      description: `Browse at your own pace — no salesperson breathing down your neck, no "what's it gonna take to get you in this car today?" Every listing has real photos, full specs, pricing, and history. You actually know what you're looking at. Revolutionary.`,
      icon: 'ri-search-line',
      color: 'teal'
    },
    {
      number: '02',
      title: 'Get Financing Approved',
      feeling: 'No awkward back-office waiting while they "run it by the manager."',
      description: `Apply in minutes from your couch. We work with multiple lenders so you get a real rate — not a "we'll figure it out when you get here" runaround. All credit types welcome. No four-hour dealership marathon. No surprise rate markup because the F&I guy has a boat payment.`,
      icon: 'ri-bank-card-line',
      color: 'teal'
    },
    {
      number: '03',
      title: 'Sign Digitally',
      feeling: 'No one will hand you a pen and say "just initial here, here, and here" without explaining anything.',
      description: `Review everything on your own time. Sign online, or we overnight the paperwork via FedEx — sign and send back. No four-hour dealership marathon. No "just one more form." No mandatory nitrogen tire package that appears out of nowhere. You're done when you say you're done.`,
      icon: 'ri-file-text-line',
      color: 'teal'
    },
    {
      number: '04',
      title: 'Delivery to Your Door',
      feeling: 'You will never have to sit in a waiting room that smells like burnt coffee and regret.',
      description: `Your vehicle shows up at your home or office, ready to drive. Delivery is often included in financing or purchase price. You didn't have to go anywhere. You didn't have to talk to anyone you didn't want to. You didn't have to pretend to be interested in the salesperson's weekend plans. That's the whole point.`,
      icon: 'ri-truck-line',
      color: 'teal'
    }
  ];

  const brokerSteps = [
    {
      number: '01',
      title: 'Submit Your Wish List',
      // Fixed the apostrophe issue by using double quotes for the outer string
      feeling: "No one will try to upsell you into a trim level you didn't ask for.",
      description: `Tell us exactly what you want — make, model, year, features, budget. Not sure yet? That's fine too. We'll help you figure it out. No pressure, no pitch, no "but have you considered the premium package?" Just a conversation about what you actually need.`,
      icon: 'ri-file-list-3-line',
      color: 'orange'
    },
    {
      number: '02',
      title: 'We Search Nationwide',
      feeling: 'While you\'re living your life, we\'re doing the part you hate.',
      description: `We're combing dealer networks, auctions, and private sellers across all 50 states. No weekends lost. No test drives you didn't ask for. No "let me show you what we have on the lot right now." We find the right match — not just the closest one or the one with the highest commission.`,
      icon: 'ri-search-2-line',
      color: 'orange'
    },
    {
      number: '03',
      title: 'We Inspect & Negotiate',
      feeling: 'We negotiate the way you would if you had 10 years of experience and zero emotional attachment to the car.',
      description: `We inspect the vehicle, verify its history, and negotiate hard — because we're not trying to make friends with the dealer. You approve every step. Nothing moves without your green light. No "this deal expires tonight" pressure. No "another buyer is looking at it right now" fiction.`,
      icon: 'ri-hand-coin-line',
      color: 'orange'
    },
    {
      number: '04',
      title: 'It Shows Up at Your Door',
      feeling: 'You skipped the entire dealership experience. Congratulations.',
      description: `Paperwork handled. Transport arranged. Your vehicle arrives ready to drive — no dealership visit, no waiting room coffee, no "just one more signature," no finance manager trying to sell you a warranty on your warranty. You literally don't have to go anywhere. One flat $995 fee. That's it.`,
      icon: 'ri-home-smile-line',
      color: 'orange'
    }
  ];

  const steps = activeTab === 'online' ? onlineSteps : brokerSteps;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Path</h2>
          <p className="text-xl text-[#374151] max-w-3xl mx-auto mb-8">
            Know what you want? Buy online. Need help finding the perfect vehicle? Use our broker service. Either way — no games, no pressure, no regrets.
          </p>

          <div className="inline-flex bg-gray-100 p-1 rounded-lg" role="tablist" aria-label="Purchase path selector">
            <button
              onClick={() => setActiveTab('online')}
              role="tab"
              aria-selected={activeTab === 'online'}
              aria-controls="panel-online"
              id="tab-online"
              className={`px-8 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer ${
                activeTab === 'online'
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'text-[#374151] hover:text-gray-900'
              }`}
            >
              <i className="ri-shopping-cart-line mr-2" aria-hidden="true"></i>
              Buy Online
            </button>
            <button
              onClick={() => setActiveTab('broker')}
              role="tab"
              aria-selected={activeTab === 'broker'}
              aria-controls="panel-broker"
              id="tab-broker"
              className={`px-8 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer ${
                activeTab === 'broker'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-[#374151] hover:text-gray-900'
              }`}
            >
              <i className="ri-user-star-line mr-2" aria-hidden="true"></i>
              Use a Broker
            </button>
          </div>
        </div>

        <div
          id={activeTab === 'online' ? 'panel-online' : 'panel-broker'}
          role="tabpanel"
          aria-labelledby={activeTab === 'online' ? 'tab-online' : 'tab-broker'}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition"
            >
              <div className="flex items-start gap-6">
                <div className={`w-16 h-16 bg-${step.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`} aria-hidden="true">
                  <i className={`${step.icon} text-3xl text-${step.color}-600`}></i>
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-bold text-${step.color}-600 mb-1`}>STEP {step.number}</div>
                  <h3 className="hiw-step-title text-2xl font-bold text-gray-900 mb-1">{step.title}</h3>
                  <p className={`hiw-step-feeling text-sm font-semibold text-${step.color}-700 italic mb-3`}>{step.feeling}</p>
                  <p className="hiw-step-description text-[#374151] leading-relaxed">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center p-8 rounded-2xl ${
          activeTab === 'online' ? 'bg-teal-50 border-2 border-teal-200' : 'bg-orange-50 border-2 border-orange-200'
        }`}>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {activeTab === 'online' ? 'Timeline: 3–7 Days' : 'Timeline: 7–14 Days'}
            </h3>
            <p className="text-[#374151] mb-6">
              {activeTab === 'online'
                ? 'From browsing to delivery, most online purchases wrap up within a week. Financing approval is fast, paperwork takes a day or two, and delivery is scheduled within 3–5 days. You\'ll spend more time picking a Netflix show than you will in a dealership. Because you won\'t be in a dealership.'
                : 'From inquiry to driveway, most broker deals close within two weeks. Search takes 2–5 days, inspection and negotiation 2–4 days, delivery 3–5 days. Meanwhile, you\'re doing literally anything else. Grocery shopping. Watching paint dry. Literally anything is better than spending a Saturday at a dealership.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {activeTab === 'online' ? (
                <a
                  href="/inventory"
                  className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-search-line"></i>
                  Start Shopping Now
                </a>
              ) : (
                <a
                  href="/broker-services"
                  className="bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  Submit Your Wish List
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
